import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { StorageService } from './../storage/storage.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ComponentType } from '@angular/cdk/portal';
import { MatDialog } from '@angular/material/dialog';
import { UserData } from '../auth/auth.service';
import { LayoutSettings } from '../edit/predefined-templates.service';

export enum LOGIN_STATE {
  STARTING,
  SUCCESSFUL,
  COMPLETE,
  FAILED,
  LOGOUT,
}

export interface StaffIframe {
  targetUrl: string;
  target: string;
  windowFeatures: string;
}

/***
 * Shared service for exposing Behavior Subjects. Please follow this example when creating a new BS.
 * @example
 * // Restrict BS access to this service only
 * private _foo = new BehaviorSubject<string>(null);
 *
 * // Expose Observable for subscription
 * readonly foo$ = this._foo.asObservable();
 *
 * // Expose method that mutates BS and triggers subscriptions
 * setFoo(bar: string) {
 *   if (this._foo.getValue() !== bar) {
 *     this._foo.next(bar);
 *   }
 * }
 * // Optional: Expose read-only value
 * get foo(): string {
 *   return this._foo.getValue();
 * }
 */
@Injectable()
export class SharedService {
  private _iOS = false;

  readonly UNABLE_TO_FETCH_SHELF_STATUS = 'Shelf Status is not available.';

  // Private BehaviorSubjects
  private _userData: BehaviorSubject<UserData> = new BehaviorSubject(undefined);
  private _title = new BehaviorSubject<string>(null);
  private _kidsCat = new BehaviorSubject<boolean>(false);
  private _openSideNavSettings = new BehaviorSubject<any>(null);
  private _staffIframe = new BehaviorSubject<StaffIframe>(null);
  private _openStaffPage = new BehaviorSubject<any>(null);
  private _pages = new BehaviorSubject<any>(null);

  libraryChangeBS: BehaviorSubject<any> = new BehaviorSubject(undefined);
  holdingsBS = new BehaviorSubject<any>(null);
  prevGoFlagKCBS = new BehaviorSubject<any>(null);
  loadGuestListBS = new BehaviorSubject<any>(null);
  opacResultsBS = new BehaviorSubject<any>(null);
  loginStatusBS = new BehaviorSubject<LOGIN_STATE>(null);
  staffMenu = new BehaviorSubject<any>(null);
  editPage = new BehaviorSubject<any>(null);
  resize$: BehaviorSubject<any> = new BehaviorSubject(null);
  liblist$ = new BehaviorSubject<any>(null);
  header$ = new BehaviorSubject<any>(null);
  footer$ = new BehaviorSubject<any>(null);
  passPatronInfo = new BehaviorSubject<any>(null);
  openEditComponent = new BehaviorSubject<any>(null);
  addRowComponent = new BehaviorSubject<any>(null);
  currentStaffMenu = new BehaviorSubject<any>(null);

  // Public Observables
  readonly userData$ = this._userData.asObservable();
  readonly title$ = this._title.asObservable();
  readonly kidsCat$ = this._kidsCat.asObservable();

  readonly openSideNavSettigs$ = this._openSideNavSettings.asObservable();
  readonly staffIframe$ = this._staffIframe.asObservable();
  readonly openStaffPage$ = this._openStaffPage.asObservable();
  readonly pages$ = this._pages.asObservable();

  private _matDialog = inject(MatDialog);
  private _router = inject(Router);
  private _snackBar = inject(MatSnackBar);
  private _storageService = inject(StorageService);

  constructor() {
    this._iOS = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
    this.setKidsCat(this._storageService.getIsKidsCat() ?? false);
  }

  // Synchronous Getters
  get systemCategory(): number {
    return this._userData.getValue()?.systemCategory;
  }

  get userData(): UserData {
    return !this._userData.getValue() ? undefined : structuredClone(this._userData.getValue());
  }

  // Update methods
  setUserData(userData: UserData) {
    this._storageService.setCid(userData.customerId.toLowerCase());
    this._storageService.setLid(userData.libraryId.toLowerCase());
    this._storageService.setUserData(userData);
    this._userData.next(userData);
  }

  static arrayUnique(array): any[] {
    return [...new Set(array)];
  }

  static dedupeArrayOfObjects(arr: any, property: string): any[] {
    return arr.filter((obj, pos, arr) => {
      return arr.map((mapObj) => mapObj[property]).indexOf(obj[property]) === pos;
    });
  }

  static isEmpty(value) {
    if (!value) {
      return true;
    }

    const results = !/\S/.test(value);
    return results;
  }

  static isNotEmpty(value) {
    return !SharedService.isEmpty(value);
  }

  static isEqualsString(str1: string, str2: string) {
    return str1?.localeCompare(str2, undefined, { sensitivity: 'accent' }) == 0;
  }

  isIOS(): boolean {
    return this._iOS;
  }

  getBrowserType(): string {
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    if (isFirefox) {
      return 'firefox';
    }
    const ua = navigator.userAgent.toLowerCase();
    if (ua.indexOf('edge') > -1) {
      return 'edge';
    }
    if (ua.indexOf('safari') != -1) {
      if (ua.indexOf('chrome') > -1) {
        return 'chrome';
      } else {
        return 'safari';
      }
    }
    return 'chrome';
  }

  setTitle(value) {
    this._title.next(value);
  }

  getResize() {
    return this.resize$.getValue();
  }

  getResizeBS() {
    return this.resize$.asObservable();
  }

  setResizeBS(value) {
    this.resize$.next(value);
  }

  setDefaultWindowSize(value): void {
    this.resize$.next(value);
  }

  setOpacResultsBS(value) {
    this.opacResultsBS.next(value);
  }

  getOpacResultsBS() {
    return this.opacResultsBS.asObservable();
  }

  getLibDropdownList() {
    return this.liblist$.getValue();
  }

  setLibDropdownList(value) {
    return this.liblist$.next(value);
  }

  /**
   * Updates the KidsCat flag in the storage service and the BehaviorSubject.
   * @param enabled
   */
  setKidsCat(enabled: boolean) {
    this._storageService.setIsKidsCat(enabled);
    this._kidsCat.next(enabled);
  }

  getIsKidsCat(): boolean {
    return this._kidsCat.value;
  }

  setLayoutSettings(value: LayoutSettings) {
    this.header$.next(value);
  }

  getLayoutSettings(): Observable<LayoutSettings> {
    return this.header$.asObservable();
  }

  setFooter(value) {
    this.footer$.next(value);
  }

  getFooter() {
    return this.footer$.asObservable();
  }

  setLoadGuestListBS(value) {
    this.loadGuestListBS.next(value);
  }

  getLoadGuestListBS() {
    return this.loadGuestListBS.asObservable();
  }

  setStaffIframeBS(value: StaffIframe) {
    this._staffIframe.next(value);
  }

  setPrevGoFlagKCBS(value) {
    this.prevGoFlagKCBS.next(value);
  }

  getPrevGoFlagKCBS() {
    return this.prevGoFlagKCBS.asObservable();
  }

  setOpenSideNavSettings(value) {
    this._openSideNavSettings.next(value);
  }

  setLoginStatusBS(value) {
    this.loginStatusBS.next(value);
  }

  getLoginStatusBS() {
    return this.loginStatusBS.asObservable();
  }

  setHoldingsBS(value) {
    this.holdingsBS.next(value);
  }

  getHoldingsBS() {
    return this.holdingsBS.asObservable();
  }

  setLibraryChangedBS(cid: string, lid: string) {
    this.libraryChangeBS.next({ cid, lid });
  }

  getLibraryChangedBS() {
    return this.libraryChangeBS.asObservable();
  }

  //This one is used to send menu data to staff menu in angular pages
  getCurrentStaffMenu() {
    return this.currentStaffMenu.asObservable();
  }

  setCurrentStaffMenu(value) {
    this.currentStaffMenu.next(value);
  }

  setOpenStaffPage(value) {
    this._openStaffPage.next(value);
  }

  getPatronInfo() {
    return this.passPatronInfo.asObservable();
  }

  setPatronInfo(value) {
    this.passPatronInfo.next(value);
  }

  setPage(value) {
    this.editPage.next(value);
  }

  getPage() {
    return this.editPage.asObservable();
  }

  setPageSlideNav(value) {
    this.openEditComponent.next(value);
  }

  getPageSlideNav() {
    return this.openEditComponent.asObservable();
  }

  setPages(value) {
    this._pages.next(value);
  }

  getRowSlideNav() {
    return this.openEditComponent.asObservable();
  }

  setRow(index) {
    this.addRowComponent.next(index);
  }

  getRow() {
    return this.addRowComponent.asObservable();
  }

  getLength(number): number {
    return number.toString().length;
  }

  insertStrAtPos(main_string, ins_string, pos) {
    if (typeof pos == 'undefined') {
      pos = 0;
    }
    if (typeof ins_string == 'undefined') {
      ins_string = '';
    }
    return main_string.slice(0, pos) + ins_string + main_string.slice(pos);
  }

  arr_diff(a1, a2) {
    const a = [],
      diff = [];
    for (let i = 0; i < a1.length; i++) {
      a[a1[i]] = true;
    }
    for (let i = 0; i < a2.length; i++) {
      if (a[a2[i]]) {
        delete a[a2[i]];
      } else {
        a[a2[i]] = true;
      }
    }
    for (const k in a) {
      diff.push(k);
    }
    return diff;
  }

  renameKeyInObject(object, oldkey, newkey) {
    if (oldkey && newkey) {
      object[newkey] = object[oldkey];
      delete object[oldkey];
    }
    return object;
  }

  updateDateForm(dateForm) {
    const date1 = dateForm.date1;
    const date2 = dateForm.date2;
    if (SharedService.isEmpty(date1) && !SharedService.isEmpty(date2)) {
      dateForm.date1 = dateForm.date2;
    }
    if (!SharedService.isEmpty(date1) && SharedService.isEmpty(date2)) {
      dateForm.date2 = dateForm.date1;
    }
    return dateForm;
  }

  updateYearPubFormObject(object) {
    const yearPub1 = object.yearPub1;
    const yearPub2 = object.yearPub2;
    if (SharedService.isEmpty(yearPub1) && !SharedService.isEmpty(yearPub2)) {
      object.yearPub1 = object.yearPub2;
    }
    if (!SharedService.isEmpty(yearPub1) && SharedService.isEmpty(yearPub2)) {
      object.yearPub2 = object.yearPub1;
    }
    return object;
  }

  isEmptyObject(obj) {
    for (const prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        return false;
      }
    }
    return JSON.stringify(obj) === JSON.stringify({});
  }

  checkObjectPropsNullOrEmpty(obj) {
    for (const key in obj) {
      if (obj[key] == null || obj[key] == '') {
        return true;
      }
    }
    return false;
  }

  replaceBetweenInString(string, start, end, what) {
    const intial = string.substring(0, start);
    const last = string.substring(end);
    const newString = intial + what + last;
    return newString;
  }

  deepCopy(data: any) {
    return JSON.parse(JSON.stringify(data));
  }

  isEqualArray(value, other) {
    // Get the value type
    const type = Object.prototype.toString.call(value);

    // If the two objects are not the same type, return false
    if (type !== Object.prototype.toString.call(other)) {
      return false;
    }

    // If items are not an object or array, return false
    if (['[object Array]', '[object Object]'].indexOf(type) < 0) {
      return false;
    }

    // Compare the length of the length of the two items
    const valueLen = type === '[object Array]' ? value.length : Object.keys(value).length;
    const otherLen = type === '[object Array]' ? other.length : Object.keys(other).length;
    if (valueLen !== otherLen) {
      return false;
    }

    // Compare properties
    if (type === '[object Array]') {
      for (let i = 0; i < valueLen; i++) {
        if (this.compare(value[i], other[i]) === false) {
          return false;
        }
      }
    } else {
      for (const key in value) {
        if (value.hasOwnProperty(key)) {
          if (this.compare(value[key], other[key]) === false) {
            return false;
          }
        }
      }
    }

    return true;
  }

  compare(item1, item2) {
    // Get the object type
    const itemType = Object.prototype.toString.call(item1);

    // If an object or array, compare recursively
    if (['[object Array]', '[object Object]'].indexOf(itemType) >= 0) {
      if (!this.isEqualArray(item1, item2)) {
        return false;
      }
    } else {
      // Otherwise, do a simple comparison
      // If the two items are not the same type, return false
      if (itemType !== Object.prototype.toString.call(item2)) {
        return false;
      }

      // Else if it's a function, convert to a string and compare
      // Otherwise, just compare
      if (itemType === '[object Function]') {
        if (item1.toString() !== item2.toString()) {
          return false;
        }
      } else {
        if (item1 !== item2) {
          return false;
        }
      }
    }
  }

  isEquivalentObjects(a, b) {
    // Create arrays of property names
    const aProps = Object.getOwnPropertyNames(a);
    const bProps = Object.getOwnPropertyNames(b);
    // If number of properties is different,
    // objects are not equivalent
    if (aProps.length != bProps.length) {
      return false;
    }
    for (let i = 0; i < aProps.length; i++) {
      const propName = aProps[i];
      // If values of same property are not equal,
      // objects are not equivalent
      console.log(a[propName]);
      console.log(b[propName]);
      if (a[propName] !== b[propName]) {
        return false;
      }
    }
    // If we made it this far, objects
    // are considered equivalent
    return true;
  }

  putMissingPropsObjectsInEnd(array, property) {
    return array.sort((a, b) => {
      if (
        !a.hasOwnProperty(property) || a[property] == null || Array.isArray(a[property])
          ? a[property].length == 0
          : a[property] === ''
      ) {
        return 1;
      } else if (
        !b.hasOwnProperty(property) || b[property] == null || Array.isArray(b[property])
          ? b[property].length == 0
          : b[property] === ''
      ) {
        return -1;
      } else {
        return 0;
      }
    });
  }

  groupBy(array, prop) {
    return array.reduce(function (groups, item) {
      const val = item[prop];
      groups[val] = groups[val] || [];
      groups[val].push(item);
      return groups;
    }, {});
  }

  isArray(obj) {
    return Array.isArray(obj);
  }

  sortArray(array: any[], property: string) {
    if (!array?.length) {
      return;
    }

    return array.sort((a, b) => {
      const aProp = a[property].toLowerCase();
      const bProp = b[property].toLowerCase();

      return aProp.localeCompare(bProp, undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  sortArrayDescending(array, property) {
    if (array.length) {
      if (isNaN(array[0][property])) {
        return array.sort((a, b) =>
          b[property].toLowerCase() > a[property].toLowerCase() ? 1 : -1,
        );
      }
      return array.sort((a, b) => (b[property] > a[property] ? 1 : -1));
    }
    return array;
  }

  addDaysToDate(date, days): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  /**
   * Scrolls and element into view, based on an ancestors overflow
   * @param element
   */
  scrollIntoView(element: any): void {
    element.scrollIntoView({ behavior: 'smooth' });
  }

  redirect404(message = ''): void {
    this._router.navigateByUrl('/404');
    if (message && message != '' && message != null) {
      this.openSnackBar(message, 'ok');
    }
  }

  getDateGMT(str: string): Date {
    return new Date(str);
  }

  doesMonthExist(issueDay, issueMonth, issueYear): boolean {
    const month = /\S/.test(issueMonth) ? issueDay : 0;
    const day = /\S/.test(issueDay) ? issueDay : 0;
    const year = /\S/.test(issueYear) ? issueYear : 0;
    if (month == 0 && day != 0 && year != 0) {
      return false;
    }
    return true;
  }

  isValidArticleYearIll(issueYear: any): boolean {
    if (issueYear) {
      if (!/\S/.test(issueYear)) {
        return true;
      }
      const year = issueYear != '' && /\S/.test(issueYear) ? issueYear : 0;
      if (isNaN(year)) {
        return false;
      } else {
        if (year == 0 || this.getLength(year) != 4) {
          return false;
        }
        return true;
      }
    }
    return true;
  }

  findPositionFromTop(obj) {
    let currenttop = 0;
    if (obj.offsetParent) {
      do {
        currenttop += obj.offsetTop;
      } while ((obj = obj.offsetParent));
      return currenttop;
    }
  }

  unableToCompleteRequestMessage() {
    this.openSnackBar('Unable to complete your request at this moment.');
  }

  showArticleMonthErrorMessage(): void {
    const message =
      'You have entered a Year and Day. Please include an issue Month or delete the Day if not needed.';
    this.openSnackBar(message, 'ok');
  }

  showArticleYearErrorMessage(showbatchMsg: boolean = false): void {
    const message = showbatchMsg
      ? 'Please enter a valid 4-digit year in the “Article Year” field.'
      : 'Please enter a valid 4-digit year in the Article Date “Year” field.';
    this.openSnackBar(message, 'ok');
  }

  logMessageAndRoute(message: string, type: string = 'error', route: string = '404'): void {
    console.error(message);
    this._router.navigateByUrl('/404');
  }

  openSnackBar(message: string, action: string = 'ok', duration = 4000) {
    this._snackBar.open(message, action, { duration: duration, verticalPosition: 'top' });
  }

  componentSnackbar(Component, message: string, extraMessage: string, location: string = ''): void {
    this._snackBar.openFromComponent(Component, {
      duration: 5000,
      verticalPosition: 'top',
      data: {
        message: message,
        extraMessage: extraMessage,
        location: location,
      },
    });
  }

  openMatDialog(
    message: string,
    cancelButton: string,
    templateComponent: ComponentType<any>,
    width: string = '',
    height: string = '',
    customData: any = null,
  ) {
    this._matDialog.open(templateComponent, {
      height: height,
      width: width,
      data:
        customData != null
          ? customData
          : {
              message: message,
              buttonText: {
                cancel: cancelButton,
              },
            },
    });
  }

  isMobile(): boolean {
    const ua = navigator.userAgent;
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua);
    return isMobile;
  }

  addScriptToIndex(id: string, source: string): Promise<Event> {
    if (document.querySelector(`#${id}`)) {
      document.querySelector(`#${id}`).remove();
    }

    return new Promise((resolve) => {
      const scriptElement = document.createElement('script');
      scriptElement.id = id;
      scriptElement.src = source;
      scriptElement.onload = resolve;
      document.getElementsByTagName('head')[0].appendChild(scriptElement);
    });
  }

  arrayCreateQueryParams(arr: any[], key: string) {
    let queryParams: string;
    for (let i = 0; i < arr.length; i++) {
      if (i === 0) {
        queryParams = '?';
      }
      queryParams += `${key}=${arr[i]}`;

      if (i !== arr.length - 1) {
        queryParams += '&';
      }
    }

    return queryParams;
  }

  getValueFromJSON(json: string, key: string) {
    const parsedObject = JSON.parse(json);
    return parsedObject[key];
  }

  openLinkInNewTab(url: string): void {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }
}
